import React from "react";

import clsx from "clsx";
import { intersection, uniq } from "lodash";
import { Link, useLocation } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import {
  useAttributedAtom,
  useCustomerTagsAtom,
  useDiscountCodesAtom,
  useOrderTagsAtom,
  useOrderTypesAtom,
  useProductsPurchasedAtom,
  useSourceNameAtom,
  useSubscriptionsAtom,
} from "@/atoms/order-page-atoms";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useLogger } from "@components/v2/analytics";
import { DataTypeProvider } from "@devexpress/dx-react-grid";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import {
  MetricFormatters,
  type SubscriptionTypeEnum,
} from "@north-beam/nb-client-side";
import {
  CustomerTypes,
  b64Encode,
  formatDateTime,
  type CustomerType,
} from "@north-beam/nb-common";
import {
  ORDER_TOUCHPOINT_LIMIT,
  ORDER_UNKNOWN_TOUCHPOINTS_QUERY_PARAM,
} from "@pages/orders/constants";
import {
  SubscriptionLabelToValueMapping,
  SubscriptionValueToLabelMapping,
} from "@pages/subscriptions-order-tags/subscription-order-tags";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { SOURCE_NAME_TAG } from "./constant";
import { TagsSelector } from "./tags-selector";

const DateFormatter = ({ value }: DataTypeProvider.ValueFormatterProps) => (
  <span className="font-sans">{formatDateTime(value.toISOString())}</span>
);

const DateEditor = () => {
  return null;
};

export const DateTypeProvider = (props: any) => (
  <DataTypeProvider
    formatterComponent={DateFormatter}
    editorComponent={DateEditor}
    {...props}
  />
);
export const dateComparator = (a: Date, b: Date) => {
  const aTime = a.getTime();
  const bTime = b.getTime();
  return aTime - bTime;
};

const SingleBadgeDataButton = ({
  className,
  value,
  onClick,
  hasTooltip = false,
}: {
  value: any;
  className?: string;
  onClick?: (value: any) => void;
  hasTooltip?: boolean;
}) => (
  <button
    className={twMerge(
      clsx(
        "rounded-[3rem] bg-cosmic-4 text-primary px-2 hover:bg-cosmic-3 font-normal font-sans flex flex-row",
      ),
      className,
    )}
    onClick={onClick}
  >
    <div className={clsx("max-w-[180px] truncate")}>{value}</div>
    {hasTooltip && <GenericTooltip content={value}></GenericTooltip>}
  </button>
);

export const SingleBadgeDataFormatter = ({
  value,
  className,
  onClick,
  shouldAnon,
  type,
  hasTooltip = false,
}: {
  value: any;
  className?: string;
  onClick?: (value: any) => void;
  shouldAnon: boolean;
  type: string;
  hasTooltip?: boolean;
}) => {
  const prefs = usePrefs();
  if (!value?.trim()) {
    return null;
  }
  if (!shouldAnon) {
    return (
      <SingleBadgeDataButton
        className={className}
        onClick={() => onClick?.(value)}
        value={value}
        hasTooltip={hasTooltip}
      />
    );
  }
  const doAnon = prefs.isDemoMode;

  const potentiallyAnoned = generateAnonBase64Hash({
    value,
    doAnon,
    prefix: type,
  });

  return (
    <SingleBadgeDataButton
      className={className}
      onClick={() => onClick?.(potentiallyAnoned)}
      value={potentiallyAnoned}
      hasTooltip={hasTooltip}
    />
  );
};

export const AttributedDataFormatter = ({
  value,
}: {
  value: any;
  className?: string;
}) => {
  const [filter, setFilter] = useAttributedAtom();
  return (
    <SingleBadgeDataFormatter
      value={value}
      onClick={(v) => setFilter(filter?.length ? uniq([...filter, v]) : [v])}
      shouldAnon={false}
      type="attributed"
    />
  );
};

export const CustomerTypeDataFormatter = ({
  value,
  className,
}: {
  value: CustomerType;
  className?: string;
}) => {
  const [filter, setFilter] = useOrderTypesAtom();

  const friendlyName = CustomerTypes.getLabel(value);
  return (
    <button
      className={clsx(
        "rounded-full bg-cosmic-4 text-primary px-2 hover:bg-cosmic-3 font-normal font-sans",
        className,
      )}
      onClick={() =>
        setFilter(
          filter?.length ? uniq([...filter, friendlyName]) : [friendlyName],
        )
      }
    >
      {friendlyName}
    </button>
  );
};

export const OrderIdDataFormatter = ({
  value,
  row,
}: DataTypeProvider.ValueFormatterProps) => {
  const location = useLocation();
  const logger = useLogger({});

  const unknownTouchpointsParam =
    row.numberOfTouchpoints > ORDER_TOUCHPOINT_LIMIT
      ? `&${ORDER_UNKNOWN_TOUCHPOINTS_QUERY_PARAM}=true`
      : "";

  return (
    <Link
      to={{
        pathname: encodeURIComponent(`${b64Encode(row.conversionEventId)}`),
        search: `${location.search}${unknownTouchpointsParam}`,
      }}
      target="_blank"
      relative="path"
      className="text-md hover-underline"
      rel="noreferrer"
      onClick={() => {
        logger.event.ordersPageOrderIdEvent("click", {
          container: "links",
          eventDetails: {
            orderId: row.orderId,
          },
        });
      }}
    >
      {row.orderId}
    </Link>
  );
};

export const OrderNumberDataFormatter = ({
  value,
  row,
}: DataTypeProvider.ValueFormatterProps) => {
  return <div>{row.orderNumber}</div>;
};

export const ValueDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  return <div>{value}</div>;
};

export const OrderTouchpointsFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  return <div>{value > ORDER_TOUCHPOINT_LIMIT ? "Not available" : value}</div>;
};

export const MultiBadgeDataFormatter = ({
  value,
  selectedValues,
  onChange,
  shouldAnon,
  type,
  visibleExpandedValues = 3,
  toOption,
}: {
  type: string;
  shouldAnon: boolean;
  value: any;
  selectedValues: string[];
  visibleExpandedValues?: number;
  onChange: (values: string[] | null) => void;
  toOption?: (tag: string) => { label: string; value: string };
}) => {
  const prefs = usePrefs();
  const doAnon = prefs.isDemoMode;

  const distinctTags = uniq<string>(value);
  const options = distinctTags.map((e) =>
    toOption ? toOption(e) : { label: e, value: e },
  );
  const values = intersection(selectedValues, distinctTags);

  return (
    <div className="flex items-center gap-2 font-sans">
      {distinctTags.length <= visibleExpandedValues && (
        <>
          {options.map((option) => (
            <SingleBadgeDataFormatter
              value={option.label}
              key={option.value}
              onClick={() => onChange(uniq([...selectedValues, option.value]))}
              shouldAnon={shouldAnon}
              type={type}
              hasTooltip
            />
          ))}
        </>
      )}
      {distinctTags.length > visibleExpandedValues && (
        <>
          <span>({distinctTags.length})</span>
          <TagsSelector
            dropdownClassName="w-[300px] z-[2005]"
            showViewTagsButton
            options={
              shouldAnon
                ? options.map((o) => {
                    const hash = generateAnonBase64Hash({
                      value: o.label,
                      prefix: type,
                      doAnon,
                    });
                    return {
                      label: hash,
                      value: hash,
                    };
                  })
                : options
            }
            values={values}
            onChange={(v) => {
              const newValues = v || [];
              const toRemove = distinctTags.filter(
                (e) => !newValues.includes(e),
              );
              const toUpdate = uniq([...selectedValues, ...newValues]);

              onChange(toUpdate.filter((e) => !toRemove.includes(e)));
            }}
          />
        </>
      )}
    </div>
  );
};

export const TagsDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const [filter, setFilter] = useOrderTagsAtom();

  return (
    <MultiBadgeDataFormatter
      value={value}
      selectedValues={filter || []}
      onChange={setFilter}
      shouldAnon={true}
      type="tag"
    />
  );
};

export const SourceNameDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const [filter, setFilter] = useSourceNameAtom();

  return (
    <MultiBadgeDataFormatter
      value={value}
      selectedValues={filter || []}
      onChange={setFilter}
      shouldAnon={true}
      type="sourceName"
      toOption={(tag) => ({
        label: tag.replace(new RegExp(`^${SOURCE_NAME_TAG}`), ""),
        value: tag,
      })}
    />
  );
};

export const CustomerTagsDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const [filter, setFilter] = useCustomerTagsAtom();

  return (
    <MultiBadgeDataFormatter
      value={value}
      selectedValues={filter || []}
      onChange={setFilter}
      shouldAnon={true}
      type="customerTag"
    />
  );
};

export const DiscountCodesDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const [filter, setFilter] = useDiscountCodesAtom();

  return (
    <MultiBadgeDataFormatter
      value={value}
      selectedValues={filter || []}
      onChange={setFilter}
      shouldAnon={true}
      type="discount"
    />
  );
};

export const ProductsPurchasedDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const [filter, setFilter] = useProductsPurchasedAtom();

  return (
    <MultiBadgeDataFormatter
      value={value}
      selectedValues={filter || []}
      onChange={setFilter}
      shouldAnon={true}
      type="product"
    />
  );
};

export const SubscriptionsDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const [filter, setFilter] = useSubscriptionsAtom();

  return (
    <SingleBadgeDataFormatter
      value={SubscriptionValueToLabelMapping[value as SubscriptionTypeEnum]}
      onClick={(v) => {
        const mappedValue = SubscriptionLabelToValueMapping[
          v as keyof typeof SubscriptionLabelToValueMapping
        ] as SubscriptionTypeEnum;

        setFilter(
          filter?.length ? uniq([...filter, mappedValue]) : [mappedValue],
        );
      }}
      shouldAnon={false}
      type="subscription"
      hasTooltip={false}
    />
  );
};

export const CurrencyDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const { user } = useUser();
  return (
    <div>
      {MetricFormatters.currency(value, user.currencies.currentCurrency, {
        precision: "exact",
      })}
    </div>
  );
};
